import React from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Grid, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as MuiIcons from '@mui/icons-material';

const getIcon = (iconName) => {
  if (!iconName) return null;
  const Icon = MuiIcons[iconName] || MuiIcons['ConfirmationNumber'];
  return <Icon sx={{ fontSize: 28 }} />;
};

const formatPrice = (item) => {
  if (item.priceLabel) return item.priceLabel;
  if (item.price === 0) return 'Free';
  return `${item.currency || 'PHP'} ${Number(item.price).toLocaleString()}`;
};

const SelectionGroup = ({ group, value, onChange, error }) => {
  const theme = useTheme();
  const { id, title, subtitle, selectionMode, items = [], layout, columns = 3 } = group;
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  const isSelected = (itemId) => selected.includes(itemId);

  const handleSelect = (itemId) => {
    if (selectionMode === 'multi') {
      const next = isSelected(itemId)
        ? selected.filter((s) => s !== itemId)
        : [...selected, itemId];
      onChange(id, next);
    } else {
      onChange(id, isSelected(itemId) ? null : itemId);
    }
  };

  const colSize = Math.floor(12 / Math.min(columns, items.length || 1));

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 32, height: 32, borderRadius: '8px',
            bgcolor: 'rgba(0,0,0,0.06)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            {group.badge || '02'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
      </Box>

      <Grid container spacing={2}>
        {items.map((item) => {
          const sel = isSelected(item.id);
          return (
            <Grid item xs={12} sm={colSize} key={item.id}>
              <Card
                onClick={() => item.available !== false && handleSelect(item.id)}
                sx={{
                  cursor: item.available === false ? 'not-allowed' : 'pointer',
                  border: sel
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  outline: sel ? 'none' : `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.2s ease',
                  opacity: item.available === false ? 0.5 : 1,
                  position: 'relative',
                  height: '100%',
                  '&:hover': item.available !== false ? {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 3px ${theme.palette.primary.main}18`,
                  } : {},
                }}
                elevation={sel ? 3 : 1}
              >
                {/* Selected check */}
                {sel && (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute', top: 10, right: 10,
                      color: theme.palette.primary.main, fontSize: 22,
                    }}
                  />
                )}

                {/* Badge */}
                {item.badge && (
                  <Chip
                    label={item.badge}
                    size="small"
                    color="primary"
                    sx={{ position: 'absolute', top: 10, right: sel ? 38 : 10, fontSize: 10 }}
                  />
                )}

                <CardContent sx={{ pb: '16px !important' }}>
                  {/* Icon */}
                  <Box
                    sx={{
                      mb: 1.5, color: sel ? theme.palette.primary.main : 'text.secondary',
                      transition: 'color 0.2s',
                    }}
                  >
                    {getIcon(item.icon)}
                  </Box>

                  {/* Title */}
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {item.title}
                  </Typography>

                  {/* Description */}
                  {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 36 }}>
                      {item.description}
                    </Typography>
                  )}

                  {/* Price (only if price field exists) */}
                  {item.price !== undefined && (
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color={item.price === 0 ? 'success.main' : 'primary.main'}
                    >
                      {formatPrice(item)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default SelectionGroup;
